import * as React from "react";

/**
 * This function is a utility for lazy loading React components.
 * It takes a dynamic import() promise and a string representing the exported component's name.
 * It returns an object with the React.lazy wrapped component.
 *
 * @param {() => Promise<I>} factory - A function that returns a promise resolving to a module with a React component.
 * @param {K} name - The name of the exported component in the module.
 * @returns {I} - An object with the lazy-loaded React component.
 *
 * Usage:
 * 1. Import the function: import { lazyImport } from './lazyImport';
 * 2. Use the function to lazy load a component:
 *    const { ComponentName } = lazyImport(() => import('./ComponentPath'), 'ComponentName');
 * 3. Now, 'ComponentName' can be used as a regular React component, but it will be loaded lazily.
 */
export function lazyImport<
  T extends React.ComponentType<any>,
  I extends { [K2 in K]: T },
  K extends keyof I
>(factory: () => Promise<I>, name: K): I {
  return Object.create({
    [name]: React.lazy(() =>
      factory().then((module) => ({ default: module[name] }))
    ),
  });
}

// Usage
// const { Home } = lazyImport(() => import("./Home"), "Home");
